import React, { useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Baby, Search, ChevronDown, ChevronUp, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import {
    MODULOS_PARVULARIA,
    INDICADORES_PARVULARIA,
    NIVEL_RIESGO_STYLE,
    RUBRICAS_PARVULARIA,
} from '../lib/parvularia-data';

const RIESGO_BADGE_CLS = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    rose: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function RubricasParvularia() {
    const [expanded, setExpanded] = useState({});
    const [q, setQ] = useState('');
    const [modFilter, setModFilter] = useState('todos');

    const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    const filtered = useMemo(() => {
        let list = INDICADORES_PARVULARIA;
        if (modFilter !== 'todos') list = list.filter((r) => r.modulo === modFilter);
        if (q) {
            const s = q.toLowerCase();
            list = list.filter((r) => (r.nombre + r.codigo).toLowerCase().includes(s));
        }
        return list;
    }, [q, modFilter]);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="rubricas-parvularia">
                {/* Header */}
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-pink-50 via-white to-sky-50 p-6">
                    <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pink-500 text-white">
                            <Baby className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-pink-700">Rúbricas oficiales 2026</div>
                            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                                Educación Parvularia · Rúbricas CPEIP
                            </h1>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-3xl">
                                Todas las rúbricas oficiales del Portafolio de Educación Parvularia 2026, organizadas por
                                módulos, con datos nacionales 2025, claves para alcanzar cada nivel y errores frecuentes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Buscar por nombre o código (ej: 4.1.6, protagonismo…)"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8 h-9"
                            data-testid="rubricas-search"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant={modFilter === 'todos' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setModFilter('todos')}
                            data-testid="filter-mod-todos"
                        >
                            Todos
                        </Button>
                        {MODULOS_PARVULARIA.map((m) => (
                            <Button
                                key={m.id}
                                variant={modFilter === m.id ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setModFilter(m.id)}
                                data-testid={`filter-${m.id}`}
                            >
                                {m.nombre}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Módulos y rúbricas */}
                {MODULOS_PARVULARIA.map((mod) => {
                    const rubricas = filtered.filter((r) => r.modulo === mod.id);
                    if (rubricas.length === 0) return null;
                    return (
                        <section key={mod.id} data-testid={`section-${mod.id}`}>
                            <div className="mb-2 flex items-center gap-2">
                                <Badge className={`${mod.color === 'indigo' ? 'bg-indigo-600' : mod.color === 'emerald' ? 'bg-emerald-600' : 'bg-amber-600'} text-white`}>
                                    {mod.nombre}
                                </Badge>
                                <h2 className="font-heading text-lg font-bold text-slate-900">{mod.titulo}</h2>
                            </div>
                            <div className="space-y-2">
                                {rubricas.map((r) => (
                                    <RubricaCard
                                        key={r.id}
                                        rubrica={r}
                                        isOpen={!!expanded[r.id]}
                                        onToggle={() => toggle(r.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    );
                })}

                {filtered.length === 0 && (
                    <Card><CardContent className="p-6 text-center text-sm text-slate-500">Sin resultados para la búsqueda.</CardContent></Card>
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500 text-center">
                    Fuente: Rúbricas Educación Parvularia 2026 (CPEIP/MINEDUC) + Análisis oficial resultados 2025.
                </div>
            </div>
        </Layout>
    );
}

function RubricaCard({ rubrica, isOpen, onToggle }) {
    const style = NIVEL_RIESGO_STYLE[rubrica.riesgo] || NIVEL_RIESGO_STYLE['Medio'];
    const isFav = rubrica.riesgo === 'Bajo';

    return (
        <Card className={`border ${isOpen ? 'border-indigo-300' : 'border-slate-200'} transition-all`}>
            <button
                onClick={onToggle}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50/60 transition"
                aria-expanded={isOpen}
                data-testid={`rubrica-toggle-${rubrica.id}`}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-bold text-slate-500">{rubrica.codigo}</span>
                        <Badge className={`${RIESGO_BADGE_CLS[style.color]} border`}>{style.emoji} Riesgo {style.label}</Badge>
                        {rubrica.es_individual && (
                            <Badge className="bg-purple-100 text-purple-800 border border-purple-200">INDIVIDUAL</Badge>
                        )}
                        {rubrica.variante && (
                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200">{rubrica.variante}</Badge>
                        )}
                    </div>
                    <h3 className="mt-1 font-heading text-base font-bold text-slate-900 leading-snug">
                        {rubrica.nombre}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-xs">
                        <span className={`inline-flex items-center gap-1 ${isFav ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {isFav ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                            <span className="font-mono font-bold">{rubrica.logrado_2025}%</span> Logrado 2025
                        </span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-600">{rubrica.por_lograr_2025}% Por lograr</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-600">Dificultad: {rubrica.dificultad}</span>
                    </div>
                </div>
                <div className="shrink-0 text-slate-400">
                    {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
            </button>

            {isOpen && (
                <div className="border-t border-slate-200 p-4 sm:p-5 space-y-4 bg-slate-50/40" data-testid={`rubrica-detail-${rubrica.id}`}>
                    {/* Datos rápidos */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <MiniStat label="Logrado 2025" value={`${rubrica.logrado_2025}%`} color="emerald" />
                        <MiniStat label="Por lograr 2025" value={`${rubrica.por_lograr_2025}%`} color="rose" />
                        <MiniStat label="Dificultad" value={rubrica.dificultad} color="amber" />
                        <MiniStat label="Prioridad" value={rubrica.prioridad} color="indigo" />
                    </div>

                    {/* Claves por nivel */}
                    <div className="grid gap-2 sm:grid-cols-3">
                        <NivelBox tit="Básico" text={rubrica.claves?.basico} color="amber" />
                        <NivelBox tit="Competente" text={rubrica.claves?.competente} color="emerald" />
                        <NivelBox tit="Destacado" text={rubrica.claves?.destacado} color="indigo" />
                    </div>

                    {/* Errores frecuentes */}
                    {rubrica.errores_frecuentes?.length > 0 && (
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-widest text-rose-700 flex items-center gap-1 mb-2">
                                <AlertTriangle className="h-3 w-3" /> Errores frecuentes documentados
                            </h4>
                            <ul className="space-y-1 text-xs text-slate-700">
                                {rubrica.errores_frecuentes.map((e, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
                                        <span className="leading-relaxed">{e}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Recomendación PDD */}
                    {rubrica.recomendacion_pdd && (
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                            <div className="text-xs font-bold text-indigo-800 mb-1">💡 Recomendación Portafolio Digital Docente</div>
                            <p className="text-xs text-indigo-900 leading-relaxed">{rubrica.recomendacion_pdd}</p>
                        </div>
                    )}

                    {/* Cambio 2025-2026 */}
                    {rubrica.cambio_2025_2026 && (
                        <div className="text-[11px] text-slate-500 italic">Cambio 2025 → 2026: {rubrica.cambio_2025_2026}</div>
                    )}
                </div>
            )}
        </Card>
    );
}

function MiniStat({ label, value, color }) {
    const cls = {
        emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
        rose: 'text-rose-700 bg-rose-50 border-rose-200',
        amber: 'text-amber-700 bg-amber-50 border-amber-200',
        indigo: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    }[color] || 'text-slate-700 bg-slate-50 border-slate-200';
    return (
        <div className={`rounded-lg border ${cls} p-2 text-center`}>
            <div className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</div>
            <div className="mt-0.5 text-xs font-bold leading-tight">{value}</div>
        </div>
    );
}

function NivelBox({ tit, text, color }) {
    const cls = {
        amber: 'border-amber-300 bg-amber-50',
        emerald: 'border-emerald-300 bg-emerald-50',
        indigo: 'border-indigo-300 bg-indigo-50',
    }[color];
    const titCls = {
        amber: 'text-amber-800',
        emerald: 'text-emerald-800',
        indigo: 'text-indigo-800',
    }[color];
    return (
        <div className={`rounded-lg border-2 ${cls} p-3`}>
            <div className={`text-[10px] font-bold uppercase tracking-widest ${titCls} mb-1`}>{tit}</div>
            <p className="text-xs text-slate-800 leading-relaxed">{text || 'Sin datos'}</p>
        </div>
    );
}
